<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex items-center justify-between px-3 py-2 border-b border-slate-600 flex-shrink-0">
      <span class="text-xs font-semibold text-slate-300 uppercase tracking-wide">3D Models</span>
      <label
        class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors cursor-pointer"
        title="Import a .glb file"
      >
        + Import
        <input type="file" accept=".glb,model/gltf-binary" class="hidden" @change="onFileSelected" />
      </label>
    </div>

    <div v-if="importError" class="px-3 py-1.5 text-xs text-red-400 border-b border-slate-700">{{ importError }}</div>

    <div v-if="pendingFile" class="px-3 py-3 bg-slate-800/70 border-b border-slate-700 space-y-3">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <form-label class="text-xs text-slate-400 mb-1">Name</form-label>
          <form-input v-model="importName" class="w-full text-sm" placeholder="Model name" />
        </div>
        <div>
          <form-label class="text-xs text-slate-400 mb-1">File</form-label>
          <div class="text-xs text-slate-400 bg-slate-900/60 rounded px-2 py-1.5 border border-slate-700 truncate">{{ pendingFile.name }}</div>
        </div>
      </div>
      <div>
        <form-label class="text-xs text-slate-400 mb-1">Description</form-label>
        <form-textarea v-model="importDescription" rows="2" class="w-full text-sm" placeholder="Optional description" />
      </div>
      <div class="flex justify-end gap-2">
        <button class="text-xs text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-slate-700" @click="cancelImport">Cancel</button>
        <button class="text-xs text-cyan-200 bg-cyan-700 hover:bg-cyan-600 px-2 py-1 rounded" @click="confirmImport">Add model</button>
      </div>
    </div>

    <ResourceListToolbar
      v-if="models.length > 0"
      :search="search"
      @update:search="search = $event"
      :sortKey="sortKey"
      @update:sortKey="sortKey = $event"
      :sortOptions="sortOptions"
    />

    <div class="flex-1 overflow-y-auto">
      <div v-if="models.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No 3D models yet.<br>
        Click <strong class="text-slate-400">+ Import</strong> to add a <strong class="text-slate-400">.glb</strong> file.
      </div>
      <div v-else-if="filteredModels.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No 3D models match your search.
      </div>

      <template v-for="m in filteredModels" :key="m.uuid">
        <ResourceListItem
          :title="m.name"
          :meta="rowMeta(m)"
          @select="editingUUID = editingUUID === m.uuid ? null : m.uuid"
        >
          <template #actions>
            <button
              class="text-slate-400 hover:text-red-400 p-1 rounded hover:bg-slate-600 transition-colors"
              title="Delete"
              @click="pendingDeleteUUID = m.uuid"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </template>
        </ResourceListItem>

        <div v-if="editingUUID === m.uuid" class="px-3 py-3 bg-slate-700/40 border-b border-slate-700 space-y-3">
          <Model3dPreview :modelId="m.uuid" />
          <div>
            <form-label class="text-xs text-slate-400 mb-1">Name</form-label>
            <form-input
              class="w-full text-sm"
              :modelValue="m.name"
              placeholder="Model name"
              @input="store.updateModel(m.uuid, { name: $event.target.value })"
            />
          </div>
          <div>
            <form-label class="text-xs text-slate-400 mb-1">Description</form-label>
            <form-textarea
              class="w-full text-sm"
              rows="2"
              :modelValue="m.description"
              placeholder="Optional description"
              @input="store.updateModel(m.uuid, { description: $event.target.value })"
            />
          </div>
          <div>
            <form-label class="text-xs text-slate-400 mb-1">Lighting quality</form-label>
            <form-select
              class="w-full text-sm"
              :modelValue="resolveQuality(m)"
              @change="setLightingQuality(m.uuid, $event.target.value)"
            >
              <option v-for="opt in LIGHTING_QUALITY_OPTIONS" :key="opt.value" :value="opt.value">
                {{ opt.label }} &mdash; {{ opt.detail }}
              </option>
            </form-select>
            <p class="text-xs text-slate-400 mt-1">
              Controls extra geometry used for smoother Point/Spot lighting on TinyGL.
              Higher quality increases H3D0 size and render cost.
            </p>
            <p v-if="compileStats(m.uuid).state === 'loading'" class="text-xs text-slate-500 mt-2">
              Compiling&hellip;
            </p>
            <p v-else-if="compileStats(m.uuid).state === 'error'" class="text-xs text-amber-400 mt-2">
              {{ compileStats(m.uuid).message }}
            </p>
            <dl v-else-if="compileStats(m.uuid).state === 'ready'" class="text-xs text-slate-400 mt-2 space-y-0.5">
              <div class="flex justify-between">
                <dt>Original triangles</dt>
                <dd class="text-slate-300">{{ compileStats(m.uuid).sourceTriangles }}</dd>
              </div>
              <div class="flex justify-between">
                <dt>Compiled triangles</dt>
                <dd class="text-slate-300">{{ compileStats(m.uuid).compiledTriangles }}</dd>
              </div>
              <div class="flex justify-between">
                <dt>H3D0 size</dt>
                <dd class="text-slate-300">{{ formatSize(compileStats(m.uuid).byteLength) }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </template>
    </div>
  </div>

  <ConfirmationModal
    :show="pendingDeleteUUID !== null"
    title="Delete 3D Model"
    :message="pendingDeleteModel ? `Are you sure you want to delete &lt;strong&gt;${pendingDeleteModel.name}&lt;/strong&gt;?` : 'Are you sure?'"
    @confirm="confirmDelete"
    @cancel="pendingDeleteUUID = null"
  />
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useModel3dStore } from '../../stores/model3dStore.js'
import { LIGHTING_QUALITY_OPTIONS, resolveLightingQuality } from '../../3d/h3dLightingQuality.js'
import { compileModel3dResourceDetailed } from '../../services/h3dResourcePipeline.js'
import ConfirmationModal from '../ConfirmationModal.vue'
import ResourceListToolbar from '../resources/ResourceListToolbar.vue'
import ResourceListItem from '../resources/ResourceListItem.vue'
import Model3dPreview from './Model3dPreview.vue'

const store = useModel3dStore()
const models = computed(() => store.all)
const importError = ref('')
const pendingFile = ref(null)
const importName = ref('')
const importDescription = ref('')
const editingUUID = ref(null)
const pendingDeleteUUID = ref(null)

const search = ref('')
const sortKey = ref('name-asc')
const sortOptions = [
  { value: 'name-asc', label: 'Name ↑' },
  { value: 'name-desc', label: 'Name ↓' },
]

const filteredModels = computed(() => {
  let list = models.value
  const q = search.value.trim().toLowerCase()
  if (q) {
    list = list.filter(m =>
      m.name.toLowerCase().includes(q) ||
      (m.description ?? '').toLowerCase().includes(q) ||
      (m.filename ?? '').toLowerCase().includes(q)
    )
  }
  list = [...list]
  if (sortKey.value === 'name-desc') list.sort((a, b) => b.name.localeCompare(a.name))
  else list.sort((a, b) => a.name.localeCompare(b.name))
  return list
})

const pendingDeleteModel = computed(() => pendingDeleteUUID.value ? store.getModel(pendingDeleteUUID.value) : null)

function onFileSelected(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  importError.value = ''
  if (!/\.glb$/i.test(file.name)) {
    importError.value = 'Only .glb files are supported.'
    return
  }
  pendingFile.value = file
  importName.value = file.name.replace(/\.glb$/i, '')
  importDescription.value = ''
}

async function confirmImport() {
  if (!pendingFile.value) return
  importError.value = ''
  try {
    editingUUID.value = await store.importGlbFile(pendingFile.value, {
      name: importName.value.trim(),
      description: importDescription.value.trim(),
    })
    cancelImport()
  } catch (e) {
    importError.value = e.message || 'Failed to import that file.'
  }
}

function cancelImport() {
  pendingFile.value = null
  importName.value = ''
  importDescription.value = ''
}

const resolveQuality = model => resolveLightingQuality(model)

// Compile results, keyed by `${uuid}:${quality}` so switching level recomputes and
// switching back is instant. Only ever populated for the expanded resource.
const compileResults = ref({})

function compileStats(uuid) {
  const model = store.getModel(uuid)
  if (!model) return { state: 'idle' }
  return compileResults.value[`${uuid}:${resolveLightingQuality(model)}`] ?? { state: 'idle' }
}

async function refreshCompileStats(uuid) {
  const model = store.getModel(uuid)
  if (!model) return
  const key = `${uuid}:${resolveLightingQuality(model)}`
  if (compileResults.value[key]) return
  compileResults.value = { ...compileResults.value, [key]: { state: 'loading' } }
  try {
    const result = await compileModel3dResourceDetailed(model, store)
    compileResults.value = { ...compileResults.value, [key]: { state: 'ready', ...result } }
  } catch (error) {
    compileResults.value = {
      ...compileResults.value,
      [key]: { state: 'error', message: error?.message || 'Compile failed' },
    }
  }
}

function setLightingQuality(uuid, quality) {
  store.updateModel(uuid, { lightingQuality: quality })
  refreshCompileStats(uuid)
}

// Compiling is only worth doing for the panel the user actually opened.
watch(editingUUID, uuid => {
  if (uuid) refreshCompileStats(uuid)
})

function rowMeta(m) {
  const parts = ['GLB', m.filename]
  if (m.metadata?.fileSize) parts.push(formatSize(m.metadata.fileSize))
  if (m.description) parts.push(m.description)
  return parts
}

function formatSize(size) {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function confirmDelete() {
  if (pendingDeleteUUID.value) {
    if (editingUUID.value === pendingDeleteUUID.value) editingUUID.value = null
    store.removeModel(pendingDeleteUUID.value)
  }
  pendingDeleteUUID.value = null
}
</script>
